import { MapSourceType } from '@caresend/types';
import { asyncTimeout, formatTimeZoneDate, generateID, arrayToObj } from '@caresend/utils';
import dayjs from 'dayjs';
import { defineStore } from 'pinia';
import { computed, watch, ref, reactive } from 'vue';
import '../../../../database/algoliaConfig/indices.js';
import { searchDenormalizedOrdersWithPagination, searchOffices, searchUsersWithPagination, searchShiftsWithPagination, needsNewKey } from '../../../../database/algoliaConfig/methods.js';
import { generatePlaceMapSources, generateWaypointMapSources, generateUserPhotoMapSources } from '../../../../database/algoliaConfig/geoSearch.js';
import { algoliaEnv } from '../../../../database/algoliaConfig/init.js';
import { mapSourcesFriendlyNames } from './model.js';
import { LARGE_USERS_RESULT_SIZE, GeoSearchFetchType, getClampedBounds, getIsInvalidBounds } from '../helpers/general.js';
import { usePortalStore } from '../store/portal/index.js';
import { unobstructed } from '../store/storeUtilities/dbSync/unobstructed.js';
import { ls } from '../store/storeUtilities/persist/persist.js';
import { autoState } from '../store/storeUtilities/state/state.js';
import { isPoint } from '../../../ui/Map/helpers/private/index.js';
import 'firebase/auth';
import { getAlgoliaPortalAPIKeyRequest } from '../../../../database/firebaseConfig/cloudFunctions.js';
import '../../../../database/firebaseConfig/init.js';
import '../../../../database/firebaseConfig/methods.js';
import '@sentry/vue';
import { toastError } from '../../../../functions/toast/toast.js';
import '@fortawesome/fontawesome-svg-core';
import 'buefy';
import '../../../ui/Icon.vue.js';
import '../../../../functions/configuration/icons.js';
import 'mobile-device-detect';
import '../../../../functions/tracking/index.js';
import '../../../../router/helpers/initRouter.js';
import { getStore } from '../../../../store/helpers/initStore.js';
import '@firebase/auth';
import 'browser-image-compression';
import 'firebase/storage';
import 'js-sha256';
import { getServiceRegionIDsByItineraryID } from '../../../../functions/itinerary/tempHelpers.js';

/**
 * This is a store for managing geo based search for itinerary creation.
 * It is used to search for places, draft waypoints, and shifts.
 * It is used for map display and search results.
 */
const addToFilters = (str, filters, combinator = 'AND') => {
    if (!filters)
        return str;
    return `${filters} ${combinator} ${str}`;
};
const getPortalAPIKey = async () => {
    var _a, _b;
    const userID = (_a = getStore().getters['auth/getUserID']) !== null && _a !== void 0 ? _a : '';
    const keyData = ls.ref(`Algolia_portal_key-${algoliaEnv}-${userID}`, null);
    if (needsNewKey(keyData.value)) {
        const newKey = await getAlgoliaPortalAPIKeyRequest();
        keyData.value = newKey;
    }
    return (_b = keyData.value) === null || _b === void 0 ? void 0 : _b.key;
};
const useItineraryGeoSearch = defineStore('itineraryGeoSearch', () => {
    const portalStore = usePortalStore();
    const serviceRegions = computed(() => portalStore.itinerary.getters.serviceRegionsFromCache(portalStore.itinerary.value.id));
    /** On any changes made to the itinerary, ensure the service regions are set */
    watch(() => portalStore.itinerary.value.waypoints, () => getServiceRegionIDsByItineraryID(portalStore.itinerary.value.id), { deep: true, immediate: true });
    const showMap = ref(true);
    const toggleShowMap = (override) => {
        showMap.value = override !== null && override !== void 0 ? override : !showMap.value;
    };
    const recentlyFetchedDraftWaypoints = ref(false);
    const useGeolocationForWaypoints = ref(false);
    const itinerary = computed(() => portalStore.itinerary.value);
    /** The initial search results */
    const initialSearchResults = {
        locations: [],
        shifts: [],
        orders: [],
        offices: [],
        users: [],
        draftWaypoints: [],
    };
    /** The search results */
    const searchResults = ref(structuredClone(initialSearchResults));
    const resetSearchResults = () => {
        searchResults.value = structuredClone(initialSearchResults);
    };
    /** Whether the search is loading */
    const loadingSearchResults = ref(false);
    /** The coordinates for the geo search */
    const coords = ref();
    /**
     * Whether the user initiated the search by clicking "Search this area" in the map view
     * or by clicking "Search" in the item container. This flag is used to prevent the search
     * from firing due to map zooming.
     */
    const userInitiatedSearch = ref(null);
    /** This is to prevent search from firing due to map zooming */
    const searchBlocked = ref(false);
    /** Set the coordinates for the geo search */
    const setCoordLimits = ({ coords: bounds, userInitiated, }) => {
        const [minLat, maxLat, minLng, maxLng] = [
            bounds.getSouth(),
            bounds.getNorth(),
            bounds.getWest(),
            bounds.getEast(),
        ];
        coords.value = { minLat, maxLat, minLng, maxLng };
        userInitiatedSearch.value = userInitiated;
    };
    const ordersPaginationMeta = autoState({
        adhocEntries: [],
        page: 0,
        pageSize: 10,
        totalHits: 0,
        totalPages: 0,
        queryString: '',
        uniqueRequestKey: null,
        filter: null,
        numericFilter: null,
        mapBounds: undefined,
        searchResultsPerPage: undefined,
    }, {
        persist: {
            lsKey: 'paginationMeta/orders',
        },
    });
    const resetOrdersPaginationMeta = () => {
        ordersPaginationMeta.set.page(0);
        ordersPaginationMeta.set.totalPages(0);
        ordersPaginationMeta.set.totalHits(0);
        ordersPaginationMeta.set.queryString('');
        ordersPaginationMeta.set.filter(null);
        ordersPaginationMeta.set.numericFilter(null);
        ordersPaginationMeta.set.mapBounds(undefined);
        ordersPaginationMeta.set.searchResultsPerPage(undefined);
    };
    const fetchMatchingOrders = async ({ filter, numericFilter, pageSize, queryString: _queryString, }) => {
        try {
            const hitsPerPage = pageSize !== null && pageSize !== void 0 ? pageSize : ordersPaginationMeta.value.pageSize;
            if (hitsPerPage <= 0)
                return;
            const uniqueRequestKey = generateID();
            ordersPaginationMeta.set.uniqueRequestKey(uniqueRequestKey);
            const key = await getPortalAPIKey();
            if (!key)
                return;
            const { hits, totalPages, currentPage, totalHits, queryString, } = await searchDenormalizedOrdersWithPagination(key, _queryString !== null && _queryString !== void 0 ? _queryString : '', {
                filters: filter,
                numericFilters: numericFilter,
                page: ordersPaginationMeta.value.page,
                hitsPerPage,
            });
            if (ordersPaginationMeta.value.uniqueRequestKey !== uniqueRequestKey)
                return;
            const shouldRefetch = hits.length === 0 && ordersPaginationMeta.value.page !== 0;
            if (shouldRefetch) {
                resetOrdersPaginationMeta();
                return fetchMatchingOrders({
                    filter,
                    numericFilter,
                    pageSize,
                    queryString: _queryString,
                });
            }
            ordersPaginationMeta.set.uniqueRequestKey(uniqueRequestKey);
            ordersPaginationMeta.set.filter(filter !== null && filter !== void 0 ? filter : null);
            ordersPaginationMeta.set.queryString(queryString || null);
            ordersPaginationMeta.set.totalPages(totalPages);
            ordersPaginationMeta.set.page(currentPage);
            ordersPaginationMeta.set.totalHits(totalHits);
            ordersPaginationMeta.set.numericFilter(numericFilter !== null && numericFilter !== void 0 ? numericFilter : null);
            if (hitsPerPage)
                ordersPaginationMeta.set.pageSize(hitsPerPage);
            searchResults.value = {
                ...initialSearchResults,
                orders: hits,
            };
            portalStore.denormalizedOrders.update(arrayToObj(hits, 'id'));
        }
        catch (error) {
            toastError(error);
        }
        /*
         * Set the recently fetched draft waypoints flag to true to
         * prevent another search from immediately executing due to map zooming
         */
        recentlyFetchedDraftWaypoints.value = true;
        setTimeout(() => {
            recentlyFetchedDraftWaypoints.value = false;
        }, 2000);
    };
    const searchOfficeByName = async (query, { type }) => {
        const key = await getPortalAPIKey();
        if (!key)
            return;
        const offices = await searchOffices(key, query, {
            filters: `type:${type}`,
        });
        searchResults.value = {
            ...initialSearchResults,
            offices: offices,
        };
        return offices;
    };
    /** Currently the only value driving a filter on users is the shift */
    const usersFilters = autoState({
        shift: null,
    });
    /**
     * The IDs of shifts that the nurse has recently declined or canceled
     * This is useful since we cant rely on the algolia data to be up to date or the
     * portal store shifts to be bound to check
     */
    const nurseJustDeclinedOrCanceledShiftIds = ref(new Set());
    const shift = computed(() => usersFilters.value.shift
        ? portalStore.shifts.get(usersFilters.value.shift.id)
        : null);
    /** Job details page: When the shift changes, update the shift assignment state */
    watch(() => usersFilters.value.shift, () => {
        portalStore.shiftAssignmentState.set.shift(usersFilters.value.shift);
    }, { immediate: true, deep: true });
    const firstWaypoint = computed(() => {
        if (!shift.value)
            return;
        return portalStore.shifts.getters.firstWaypoint(shift.value.id);
    });
    const serviceRegionCenter = computed(() => {
        if (!shift.value)
            return;
        return portalStore.shifts.getters.serviceRegionsCentroid(shift.value.id);
    });
    const defaultGeoSearchQuery = computed(() => {
        var _a;
        let { location } = (_a = firstWaypoint.value) !== null && _a !== void 0 ? _a : {};
        if (!(location === null || location === void 0 ? void 0 : location.lat) || !(location === null || location === void 0 ? void 0 : location.lng)) {
            if (!serviceRegionCenter.value)
                return {};
            location = serviceRegionCenter.value;
        }
        return {
            aroundLatLng: `${location.lat}, ${location.lng}`,
            aroundRadius: 80000, // 80km
        };
    });
    /**
     * Returns the geo search query for the users fetch. We use the map bounds if
     * available, otherwise we use the service region center of the first waypoint
     * If the user is zoomed out far enough that they likely want to see all users,
     * we return an empty geo search query.
     */
    const getUsersGeoSearchQuery = (mapBounds) => {
        if (!shift.value)
            return {};
        if (!mapBounds)
            return defaultGeoSearchQuery.value;
        const clampedBounds = getClampedBounds(mapBounds);
        const isInvalidBounds = getIsInvalidBounds(clampedBounds);
        if (isInvalidBounds)
            return {};
        return {
            insideBoundingBox: [
                [clampedBounds.minLat, clampedBounds.minLng, clampedBounds.maxLat, clampedBounds.maxLng],
            ],
        };
    };
    const usersPaginationMeta = autoState({
        adhocEntries: [],
        queryString: '',
        page: 0,
        pageSize: 10,
        totalHits: 0,
        totalPages: 0,
        uniqueRequestKey: null,
        filter: null,
        numericFilter: null,
        mapBounds: undefined,
        searchResultsPerPage: undefined,
    });
    const resetUsersPaginationMeta = () => {
        usersPaginationMeta.set.queryString('');
        usersPaginationMeta.set.page(0);
        usersPaginationMeta.set.totalPages(0);
        usersPaginationMeta.set.totalHits(0);
        usersPaginationMeta.set.filter(null);
        usersPaginationMeta.set.numericFilter(null);
        usersPaginationMeta.set.mapBounds(undefined);
        usersPaginationMeta.set.searchResultsPerPage(LARGE_USERS_RESULT_SIZE);
    };
    /**
     * Fetches users from algolia. The search is performed with different geo
     * search parameters depending on the fetch type.
     *
     * 1. Inital mount:
     *    a. Clear metadata
     *    b. No map bounds as we won't reliably have them during map zooming so we
     *       use the `defaultGeoSearchQuery` to search close to the shift
     * 2. Reload:
     *    a. Clear metadata
     *    b. Set the new map bounds for subsequent searches
     * 3. Pagination:
     *    a. Use the existing map bounds to prevent using different bounds caused
     *       by map auto-fitting to the search results on a given page
     */
    const fetchMatchingUsers = async ({ filter, queryString, pageSize, numericFilter, fetchType, }) => {
        var _a;
        if (fetchType !== GeoSearchFetchType.PAGINATION)
            resetUsersPaginationMeta();
        if (fetchType === GeoSearchFetchType.FIRST_FETCH) {
            usersPaginationMeta.set.mapBounds(undefined);
        }
        if (fetchType === GeoSearchFetchType.RELOAD) {
            usersPaginationMeta.set.mapBounds(coords.value);
        }
        if (fetchType === GeoSearchFetchType.PAGINATION) {
            const { mapBounds } = usersPaginationMeta.value;
            if (!mapBounds)
                usersPaginationMeta.set.mapBounds(coords.value);
        }
        // Not being calculated right now, but could be used to set the number of
        // algolia results based on certain criteria in `getNumberOfSearchResultsPerPage`
        usersPaginationMeta.set.searchResultsPerPage(LARGE_USERS_RESULT_SIZE);
        try {
            const hitsPerPage = pageSize !== null && pageSize !== void 0 ? pageSize : usersPaginationMeta.value.pageSize;
            if (hitsPerPage <= 0)
                return;
            const uniqueRequestKey = generateID();
            usersPaginationMeta.set.uniqueRequestKey(uniqueRequestKey);
            const key = await getPortalAPIKey();
            if (!key)
                return;
            let filters = filter !== null && filter !== void 0 ? filter : '';
            const staffingOffices = (_a = shift.value) === null || _a === void 0 ? void 0 : _a.staffingOffices;
            if (staffingOffices) {
                const filterIncludesOfficeIds = filters.includes('officeIDs:');
                if (!filterIncludesOfficeIds) {
                    const uniqueOfficeIds = [...new Set(Object.keys(staffingOffices))];
                    let officesFilter = '';
                    uniqueOfficeIds.forEach((id) => {
                        officesFilter = addToFilters(`officeIDs:${id}`, officesFilter, 'OR');
                    });
                    if (officesFilter) {
                        officesFilter = `(${officesFilter})`;
                        filters = addToFilters(officesFilter, filters, 'AND');
                    }
                }
            }
            const { hits, totalPages, currentPage, totalHits, queryString: ackQueryString, } = await searchUsersWithPagination(key, queryString !== null && queryString !== void 0 ? queryString : '', {
                filters,
                page: usersPaginationMeta.value.page,
                hitsPerPage,
                numericFilters: numericFilter,
                ...getUsersGeoSearchQuery(usersPaginationMeta.value.mapBounds),
            });
            if (usersPaginationMeta.value.uniqueRequestKey !== uniqueRequestKey)
                return;
            const shouldRefetch = hits.length === 0 && usersPaginationMeta.value.page !== 0;
            if (shouldRefetch) {
                return fetchMatchingUsers({
                    filter,
                    queryString,
                    pageSize,
                    numericFilter,
                    fetchType: GeoSearchFetchType.FIRST_FETCH,
                });
            }
            usersPaginationMeta.set.uniqueRequestKey(uniqueRequestKey);
            usersPaginationMeta.set.filter(filters !== null && filters !== void 0 ? filters : null);
            usersPaginationMeta.set.queryString(ackQueryString || null);
            usersPaginationMeta.set.totalPages(totalPages);
            usersPaginationMeta.set.page(currentPage);
            usersPaginationMeta.set.totalHits(totalHits);
            usersPaginationMeta.set.numericFilter(numericFilter !== null && numericFilter !== void 0 ? numericFilter : null);
            if (hitsPerPage)
                usersPaginationMeta.set.pageSize(hitsPerPage);
            searchResults.value = {
                ...initialSearchResults,
                users: hits,
            };
        }
        catch (error) {
            console.error('error fetching users', error);
            toastError(error);
        }
    };
    /** The shift filters for the geo search */
    const shiftFilters = reactive({
        itineraryDate: undefined,
        serviceRegionIDs: [],
    });
    const getAlgoliaShiftFilters = (filter, numericFilter) => {
        const filters = filter !== null && filter !== void 0 ? filter : '';
        const numericFilters = numericFilter !== null && numericFilter !== void 0 ? numericFilter : '';
        return {
            filters,
            numericFilters,
        };
    };
    const shiftPaginationMeta = autoState({
        adhocEntries: [],
        page: 0,
        pageSize: 10,
        totalHits: 0,
        totalPages: 0,
        queryString: '',
        uniqueRequestKey: null,
        filter: null,
        numericFilter: null,
        mapBounds: undefined,
        searchResultsPerPage: undefined,
    }, {
        persist: {
            lsKey: 'paginationMeta/shifts',
        },
    });
    /**
     * Merges entries added on the fly with the hits array if there are any
     * This is necessary because adhoc entries are not always returned in the hits array
     * and we need to keep them interleaved with the shifts in the order they were created
     */
    const maybeMergeShiftResponseWithAdhoc = (payload) => {
        let { hits, totalHits, totalPages, } = payload;
        const { currentPage, filteredAdhocEntryIDs, adhocEntries } = payload;
        /** If there are no adhoc entries, we can just return the response as is */
        if ((filteredAdhocEntryIDs === null || filteredAdhocEntryIDs === void 0 ? void 0 : filteredAdhocEntryIDs.length) === 0
            || !filteredAdhocEntryIDs
            || !adhocEntries) {
            return {
                hits,
                currentPage,
                totalHits,
                totalPages,
            };
        }
        const today = dayjs().startOf('day').valueOf();
        const hitMap = new Map(hits.map((s) => [s.id, s]));
        const filteredAdhocEntries = new Map(filteredAdhocEntryIDs === null || filteredAdhocEntryIDs === void 0 ? void 0 : filteredAdhocEntryIDs.map((id) => [id.id, id]));
        /** Remove adhoc entries that are already in the hits array and add all adhoc entries to map */
        const indexRm = new Set();
        /** 30 seconds TTL for adhoc entries */
        const TTL = dayjs().subtract(30, 'seconds').valueOf();
        adhocEntries === null || adhocEntries === void 0 ? void 0 : adhocEntries.forEach((id, i) => {
            if (!(id === null || id === void 0 ? void 0 : id.timestamp)
                || hitMap.has(id.id)
                || id.timestamp < TTL)
                indexRm.add(i);
        });
        /** Add adhoc entries to map that are not already in the hits array */
        const realHitsLength = hits.length;
        shiftPaginationMeta.set.adhocEntries(adhocEntries.filter((id, i) => {
            if (indexRm.has(i))
                return false;
            if (!hitMap.has(id.id)) {
                const s = portalStore.shifts.getRaw(id.id);
                if (!s)
                    return false;
                if (!filteredAdhocEntries.has(id.id))
                    return false;
                hitMap.set(id.id, s);
            }
            return true;
        }));
        const hitsToSort = Array.from(hitMap.values());
        /**
         * If the length of hitsToSort is less than or equal to hitsPerPage, we can return the hits as is
         * This would be the case if all previously stored adhoc entries were found in the hits array
         * so no new adhoc entries were added
         */
        const hitsPerPage = shiftPaginationMeta.value.pageSize;
        if (hitsToSort.length <= hitsPerPage && currentPage !== 0) {
            return {
                hits,
                currentPage,
                totalHits,
                totalPages,
            };
        }
        totalHits += (filteredAdhocEntryIDs.length - indexRm.size);
        totalPages = Math.ceil(totalHits / hitsPerPage);
        const firstEntry = hitsToSort[0];
        /**
         * Hits should be sorted ascending by absolute value of diff between startTime and today
         * This keeps the adhoc entries interleaved with the shifts in the order they were created
         * and is a consistent way to sort either past or future shifts
         */
        hits = hitsToSort.sort((a, b) => {
            if (!a.startTime.timestamp || !b.startTime.timestamp)
                return 0;
            const aDiff = Math.abs(a.startTime.timestamp - today);
            const bDiff = Math.abs(b.startTime.timestamp - today);
            return aDiff - bDiff;
        });
        /**
         * If we're not on the first page, we need to skip any
         * adhoc entries until we hit the first shift since it's unreliable to assume
         * any of these entries would be in the sorted order of the hits
         */
        if (currentPage !== 0 && realHitsLength) {
            const firstShiftIndex = hits.findIndex(({ id }) => id === (firstEntry === null || firstEntry === void 0 ? void 0 : firstEntry.id));
            hits.splice(0, firstShiftIndex);
        }
        hits = hits.slice(0, hitsPerPage);
        return {
            hits,
            currentPage,
            totalHits,
            totalPages,
        };
    };
    const resetShiftPaginationMeta = () => {
        shiftPaginationMeta.set.page(0);
        shiftPaginationMeta.set.totalPages(0);
        shiftPaginationMeta.set.totalHits(0);
        shiftPaginationMeta.set.filter(null);
    };
    const fetchMatchingShifts = async ({ filter, numericFilter, pageSize, reverse, isMatch, }) => {
        const hitsPerPage = pageSize !== null && pageSize !== void 0 ? pageSize : shiftPaginationMeta.value.pageSize;
        if (hitsPerPage <= 0)
            return;
        const uniqueRequestKey = generateID();
        shiftPaginationMeta.set.uniqueRequestKey(uniqueRequestKey);
        const { adhocEntries } = shiftPaginationMeta.value;
        const filteredAdhocEntryIDs = isMatch ? adhocEntries === null || adhocEntries === void 0 ? void 0 : adhocEntries.filter(({ id }) => isMatch(id)) : undefined;
        const { numericFilters, filters } = getAlgoliaShiftFilters(filter, numericFilter);
        const { key } = await getAlgoliaPortalAPIKeyRequest();
        const response = await searchShiftsWithPagination(key, '', {
            numericFilters,
            filters,
            page: shiftPaginationMeta.value.page,
            hitsPerPage,
        }, reverse);
        if (shiftPaginationMeta.value.uniqueRequestKey !== uniqueRequestKey)
            return;
        const { hits, currentPage, totalHits, totalPages } = maybeMergeShiftResponseWithAdhoc({
            ...response,
            filteredAdhocEntryIDs,
            adhocEntries,
        });
        const shouldRefetch = hits.length === 0 && shiftPaginationMeta.value.page !== 0;
        if (shouldRefetch) {
            resetShiftPaginationMeta();
            return fetchMatchingShifts({
                filter,
                numericFilter,
                pageSize: hitsPerPage,
                reverse,
                isMatch,
            });
        }
        shiftPaginationMeta.set.totalPages(totalPages);
        shiftPaginationMeta.set.page(currentPage);
        shiftPaginationMeta.set.totalHits(totalHits);
        shiftPaginationMeta.set.filter(filter !== null && filter !== void 0 ? filter : null);
        shiftPaginationMeta.set.numericFilter(numericFilter !== null && numericFilter !== void 0 ? numericFilter : null);
        if (pageSize)
            shiftPaginationMeta.set.pageSize(pageSize);
        /** If the user is a nurse, we need to filter out shifts that have been declined */
        const matchedShifts = hits;
        searchResults.value = {
            ...initialSearchResults,
            shifts: matchedShifts,
        };
        return matchedShifts;
    };
    const placeFilters = reactive({
        placeGroupTypeID: undefined,
    });
    const placeMapSources = computed(() => generatePlaceMapSources(searchResults.value.locations));
    const draftWaypointMapSources = computed(() => generateWaypointMapSources(searchResults.value.draftWaypoints, firstWaypoint.value ? [firstWaypoint.value] : []));
    const usersMapSources = computed(() => generateUserPhotoMapSources(searchResults.value.users, usersFilters.value.shift));
    const mapSources = ref([]);
    const updateMapSources = () => {
        mapSources.value = [
            ...placeMapSources.value,
            ...draftWaypointMapSources.value,
            ...usersMapSources.value,
        ].filter((source) => {
            var _a;
            const geo = (_a = source.data[0]) === null || _a === void 0 ? void 0 : _a.geometry;
            if (!isPoint(geo))
                return false;
            /** Making the executive decision to ignore (0, 0) coordinates */
            return !!geo.coordinates[0] && !!geo.coordinates[1];
        });
    };
    watch(() => portalStore.itinerary.value.id, () => {
        mapSources.value = [];
    });
    let updateLock = false;
    watch([
        placeMapSources,
        draftWaypointMapSources,
        usersMapSources,
    ], async (newValues) => {
        if (!newValues.length) {
            updateMapSources();
            return;
        }
        if (updateLock)
            return;
        updateLock = true;
        await asyncTimeout(1000);
        unobstructed(() => {
            updateMapSources();
            updateLock = false;
        }, {
            threshold: 50,
            maxTime: 10000,
        });
    }, {
        immediate: true,
    });
    const mapHighlightID = ref(null);
    const setMapHighlightID = (id) => {
        mapHighlightID.value = id;
    };
    watch([
        () => serviceRegions.value,
        () => itinerary.value.dateInfo.date,
    ], async () => {
        var _a;
        shiftFilters.itineraryDate = (_a = itinerary.value) === null || _a === void 0 ? void 0 : _a.dateInfo.date;
        shiftFilters.serviceRegionIDs = Object.keys(serviceRegions.value);
    }, { deep: true });
    const nurseLastRecordedLocation = computed(() => {
        var _a, _b, _c, _d, _e;
        if (!((_a = itinerary.value) === null || _a === void 0 ? void 0 : _a.nurseID))
            return 'No recorded location';
        const nurse = (_b = portalStore.users.get(itinerary.value.nurseID)) !== null && _b !== void 0 ? _b : getStore().getters['users/getUserByID'](itinerary.value.nurseID);
        const lastRecordedUpdate = (_d = (_c = nurse === null || nurse === void 0 ? void 0 : nurse.location) === null || _c === void 0 ? void 0 : _c.current) === null || _d === void 0 ? void 0 : _d.lastUpdate;
        if (!lastRecordedUpdate)
            return 'No recorded location';
        let timeFormat = 'h:mm a';
        if (((_e = nurse.location) === null || _e === void 0 ? void 0 : _e.current.timeZone) === dayjs.tz.guess()) {
            timeFormat = `${timeFormat} z`;
        }
        let dateFormat = '';
        if (dayjs(lastRecordedUpdate.timestamp).day() !== dayjs().day()) {
            dateFormat = 'M/DD';
        }
        const isYesterday = dayjs(lastRecordedUpdate.timestamp)
            .startOf('day')
            .diff(dayjs().startOf('day'), 'day') === 1;
        if (isYesterday) {
            dateFormat = 'Yesterday';
        }
        else if (dayjs(lastRecordedUpdate.timestamp).year() !== dayjs().year()) {
            dateFormat = 'MM/DD/YYYY';
        }
        dateFormat = dateFormat ? ` (${dateFormat})` : '';
        const formatted = `${timeFormat}${dateFormat}`;
        return `as of ${formatTimeZoneDate(lastRecordedUpdate, formatted)}`;
    });
    const hasSourceType = (sourceType) => mapSources.value.some((source) => source.id === sourceType);
    const dispatchMapLegend = computed(() => [
        {
            label: mapSourcesFriendlyNames[MapSourceType.ITINERARY_INDICATED_START_POINT],
            color: 'dark-grey',
            disabled: hasSourceType(MapSourceType.ITINERARY_ACTUAL_START_POINT),
        },
        {
            label: mapSourcesFriendlyNames[MapSourceType.ITINERARY_ACTUAL_START_POINT],
            color: 'blue',
            disabled: hasSourceType(MapSourceType.ITINERARY_INDICATED_START_POINT),
        },
        {
            label: mapSourcesFriendlyNames[MapSourceType.PATIENT],
            color: 'orange',
            disabled: hasSourceType(MapSourceType.PATIENT),
        },
        {
            label: mapSourcesFriendlyNames[MapSourceType.PICK_DROP],
            color: 'light-blue',
            disabled: hasSourceType(MapSourceType.PICK_DROP),
        },
        {
            label: mapSourcesFriendlyNames[MapSourceType.CLINICIAN_LAST_LOCATION],
            bubbleText: nurseLastRecordedLocation.value,
            color: 'red',
            disabled: hasSourceType(MapSourceType.CLINICIAN_LAST_LOCATION),
        },
        {
            label: mapSourcesFriendlyNames[MapSourceType.ITINERARY_END_POINT],
            color: 'green',
            disabled: hasSourceType(MapSourceType.ITINERARY_END_POINT),
        },
        {
            label: mapSourcesFriendlyNames[MapSourceType.DRAFT_WAYPOINTS],
            color: 'orange',
            disabled: hasSourceType(MapSourceType.DRAFT_WAYPOINTS),
        },
        {
            label: mapSourcesFriendlyNames[MapSourceType.PICK_DROP],
            color: 'light-blue',
            disabled: hasSourceType(MapSourceType.PICK_DROP),
        },
    ]);
    const collection = computed(() => ({
        type: 'FeatureCollection',
        features: [],
    }));
    return {
        searchOfficeByName,
        collection,
        dispatchMapLegend,
        fetchMatchingOrders,
        fetchMatchingShifts,
        fetchMatchingUsers,
        loadingSearchResults,
        mapHighlightID,
        mapSources,
        nurseJustDeclinedOrCanceledShiftIds,
        placeFilters,
        resetShiftPaginationMeta,
        resetUsersPaginationMeta,
        resetOrdersPaginationMeta,
        resetSearchResults,
        searchBlocked,
        searchResults,
        setCoordLimits,
        setMapHighlightID,
        shiftFilters,
        shiftPaginationMeta,
        usersFilters,
        showMap,
        toggleShowMap,
        useGeolocationForWaypoints,
        usersPaginationMeta,
        ordersPaginationMeta,
    };
});

export { useItineraryGeoSearch };
